import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import SEO from "../components/SEO";

import HeroImg    from "../assets/images/lyon2.webp";
import Show1Img   from "../assets/images/pictures_gallery/show1.webp";
import MariageImg from "../assets/images/mariage.webp";
import PortraitImg from "../assets/images/portrait.webp";
import ShootingImg from "../assets/images/shooting.webp";

const categories = [
  {
    label: "Concert",
    href:  "/gallery/concert/1",
    img:   Show1Img,
    alt:   "Photographie de concert — scène et lumières",
  },
  {
    label: "Mariage",
    href:  "/gallery/mariage/1",
    img:   MariageImg,
    alt:   "Photographie de mariage — couple en célébration",
  },
  {
    label: "Portrait",
    href:  "/gallery/portrait/1",
    img:   PortraitImg,
    alt:   "Portrait photographique en studio ou extérieur",
  },
  {
    label: "Shooting",
    href:  "/gallery/shooting/1",
    img:   ShootingImg,
    alt:   "Shooting photo mode et lifestyle",
  },
];

function HomePage() {
  const prefersReduced = useReducedMotion();

  const inViewFadeUp = (delay = 0) =>
    prefersReduced
      ? {}
      : {
          initial:   { opacity: 0, y: 20 },
          whileInView: { opacity: 1, y: 0 },
          viewport:  { once: true, margin: "-60px" },
          transition: { duration: 0.55, ease: [0.25, 0.1, 0.25, 1], delay },
        };

  const heroReveal = (delay = 0) =>
    prefersReduced
      ? {}
      : {
          initial:    { y: '110%' },
          animate:    { y: '0%' },
          transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1], delay },
        };

  const heroFade = (delay = 0) =>
    prefersReduced
      ? {}
      : {
          initial:    { opacity: 0 },
          animate:    { opacity: 1 },
          transition: { duration: 0.7, ease: 'easeOut', delay },
        };

  return (
    <main className="home-page">
      <SEO
        path="/"
        description="Photographe professionnel à Lyon. Mariages, concerts, portraits, shootings — Alexandre Jack capture vos moments avec authenticité."
      />
      <Navigation />

      {/* ── HERO ─────────────────────────────────────── */}
      <section className="hero" aria-label="Introduction">
        <div className="hero__bg" aria-hidden="true">
          <motion.img
            src={HeroImg}
            alt=""
            loading="eager"
            decoding="async"
            {...(prefersReduced ? {} : {
              initial:    { scale: 1.07 },
              animate:    { scale: 1 },
              transition: { duration: 2.4, ease: [0.25, 0.1, 0.25, 1] },
            })}
          />
        </div>
        <div className="hero__overlay" aria-hidden="true" />

        <div className="hero__content">
          <motion.p className="hero__eyebrow" {...heroFade(0.05)}>
            Photographe&nbsp;·&nbsp;Lyon
          </motion.p>

          <h1 className="hero__name">
            <span className="hero__name-line">
              <motion.span {...heroReveal(0.2)}>Alexandre</motion.span>
            </span>
            <span className="hero__name-line">
              <motion.span {...heroReveal(0.36)}>Jack</motion.span>
            </span>
          </h1>

          <span className="hero__tagline-wrap">
            <motion.p className="hero__tagline" {...heroReveal(0.52)}>
              Cliché
            </motion.p>
          </span>

          <motion.p className="hero__disciplines" {...heroFade(0.7)}>
            Portraits&nbsp;·&nbsp;Concerts&nbsp;·&nbsp;Mariages&nbsp;·&nbsp;Paysages
          </motion.p>

          <motion.div {...heroFade(0.85)}>
            <Link to="/prestation" className="hero__cta">
              Explorer le portfolio&nbsp;→
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── CATEGORIES ───────────────────────────────── */}
      <section className="categories" aria-label="Portfolio — catégories">
        <div className="categories__header">
          <motion.h2 className="categories__title" {...inViewFadeUp(0)}>
            Portfolio
          </motion.h2>
          <motion.p className="categories__subtitle" {...inViewFadeUp(0.06)}>
            Explorez mes univers
          </motion.p>
        </div>

        <ul className="categories__grid">
          {categories.map(({ label, href, img, alt }, i) => (
            <motion.li key={label} {...inViewFadeUp(i * 0.06)}>
              <a
                href={href}
                className="category-tile"
                aria-label={`Voir la galerie ${label}`}
              >
                <img src={img} alt={alt} loading="lazy" decoding="async" />
                <div className="category-tile__overlay" aria-hidden="true" />
                <div className="category-tile__label">
                  <span>{label}</span>
                  <span className="arrow" aria-hidden="true">→</span>
                </div>
              </a>
            </motion.li>
          ))}
        </ul>
      </section>

    </main>
  );
}

export default HomePage;
